import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import {
  getConsent,
  setConsent,
  REOPEN_BANNER_EVENT,
  type ConsentStatus,
} from '@/lib/cookie-consent';

export function CookieBanner() {
  const [visible, setVisible] = useState(() => getConsent().status === 'unset');

  useEffect(() => {
    function onReopen() {
      setVisible(true);
    }
    window.addEventListener(REOPEN_BANNER_EVENT, onReopen);
    return () => window.removeEventListener(REOPEN_BANNER_EVENT, onReopen);
  }, []);

  if (!visible) return null;

  function decide(status: 'accepted' | 'essential') {
    const prev: ConsentStatus = getConsent().status;
    setConsent(status);
    setVisible(false);
    // If the user is changing a prior decision (in particular, withdrawing
    // consent after analytics had already started this session), reload so
    // running timers and module state are torn down. First-time decisions
    // don't need a reload — main.tsx's subscriber initializes analytics on
    // a fresh acceptance.
    if (prev !== 'unset' && prev !== status) {
      window.location.reload();
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      // Sits on the bottom edge, so under `viewport-fit=cover` its buttons
      // would fall under the home indicator without the inset.
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 shadow-lg pb-safe px-safe"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-foreground sm:max-w-3xl">
          <p id="cookie-banner-title" className="font-medium mb-1">
            Cookies and similar technologies
          </p>
          <p className="text-muted-foreground">
            We use strictly necessary cookies to operate the Service (such as authentication and
            security). With your permission, we also use optional cookies and storage to remember
            your preferences and analyze how the Service is used. See our{' '}
            <Link
              to="/legal/privacy"
              className="underline underline-offset-2 hover:text-foreground"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </div>
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:flex-shrink-0">
          <Button variant="outline" size="sm" onClick={() => decide('essential')}>
            Essential only
          </Button>
          <Button size="sm" onClick={() => decide('accepted')}>
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
}
